import { FGListen, FGSelectInput, FGTextInput, FieldGroup, useApiEffect } from "nsitools-react";
import * as React from "react";
import * as Yup from "yup";

import { CountryApi, EPlatform, PersonApi, PersonDto } from "../../../../api";
import { FGCountrySelect, FGPhoneInput } from "../../../../components";
import { useGlobalData } from "../../../../contexts";
import { useApiService, useCrudApi, useTl } from "../../../../hooks";
import { useReferential } from "../../../../hooks/useReferential";
import { ETLCodes } from "../../../../locales";
import { DrupalFormGenerator } from "../../../common/DrupalFormGenerator";
import { DrupalFieldSet } from "../../../common/DurpalFieldSet";
import { EDrupalRoutes } from "../../../EDrupalRoutes";

export interface IDrupalProfileDetailTabProps {}

export const DrupalProfileDetailTab: React.FunctionComponent<IDrupalProfileDetailTabProps> = () => {
  const api = useApiService(PersonApi);
  const countryApi = useApiService(CountryApi);
  const { t } = useTl();
  const { currentLanguage } = useGlobalData();

  const [postalCode, setPostalCode] = React.useState("");

  const [isBelgium, setIsBelgium] = React.useState<boolean>(false);

  const [baseCountryId, baseCountryLoading] = useApiEffect(async () => await countryApi.getBelgianId(), []);
  const [languages, lloading] = useReferential((a) => a.getLanguages(), true, []);
  const [postalCodes, pcloading] = useReferential((a) => a.getStringifiedBelgianPostalCodes(), true, []);
  const [localities, locloading] = useReferential(
    (a) => a.getStringifiedBelgianLocalityFromPostalCode(postalCode ?? ""),
    false,
    [postalCode]
  );

  const [personSalutations, ploading] = useReferential(
    (a) => a.getPersonSalutations(currentLanguage.languageId),
    true,
    [currentLanguage.languageId]
  );

  const { data, loading, saveItem, saving } = useCrudApi(
    React.useMemo(
      () => ({
        getApiFn: () => api.getDrupalPersonProfile(),
        saveApiFn: (d: PersonDto) => api.save(EPlatform.PORTAL, d),
        onSavedRoute: () => `${EDrupalRoutes.profile}/detail`,
      }),
      [api]
    ),
    [baseCountryId]
  );

  React.useEffect(() => {
    if (!loading && !postalCode) {
      setPostalCode(data?.postalCode);
    }
  }, [data, loading, postalCode]);

  const FormSchema = React.useMemo(() => {
    return Yup.object().shape({
      languageId: Yup.number().nullable().required(t(ETLCodes.Required)),
      personHonorificId: Yup.string().nullable().required(t(ETLCodes.Required)), //number().required(),
      street: Yup.string().nullable().required(t(ETLCodes.Required)),
      number: Yup.string().nullable().required(t(ETLCodes.Required)),
      hkNumber: Yup.string().nullable(),
      postalCode: Yup.string().nullable().required(t(ETLCodes.Required)),
      countryId: Yup.number()
        .nullable()
        .required(t(ETLCodes.Required))
        .test("is-vat-be", t(ETLCodes.VATNotCorrespondingToCountry), async function (value) {
          if (!this.parent.vatNumber) return true;
          const belgium = await countryApi.isBelgian(value);
          return !belgium.value || (belgium.value && this.parent.vatNumber.substr(0, 2) === "BE");
        }),
      city: Yup.string().nullable().required(t(ETLCodes.Required)),
      phoneNumber: Yup.string()
        .nullable()
        .test("required", t(ETLCodes.Required), async function (value) {
          if (
            (this.parent.email === undefined || this.parent.email === null) &&
            (this.parent.mobilePhoneNumber === undefined || this.parent.mobilePhoneNumber === null)
          ) {
            return value !== null && value !== undefined;
          }
          return true;
        }),
      mobilePhoneNumber: Yup.string()
        .nullable()
        .test("required", t(ETLCodes.Required), async function (value) {
          if (
            (this.parent.phoneNumber === undefined || this.parent.phoneNumber === null) &&
            (this.parent.email === undefined || this.parent.email === null)
          ) {
            return value !== null && value !== undefined;
          }
          return true;
        }),
    });
  }, [countryApi, t]);

  return (
    !baseCountryLoading && (
      <>
        <DrupalFormGenerator
          initialValues={data}
          onSubmit={saveItem}
          editMode={true}
          loading={loading}
          saving={saving}
          validationSchema={FormSchema}
        >
          <FGListen
            field="countryId"
            onChanged={(value, formik) => {
              if (!value) {
                formik.setFieldValue("countryId", baseCountryId);
                setIsBelgium(true);
              } else {
                if (value === baseCountryId) {
                  setIsBelgium(true);
                } else {
                  setIsBelgium(false);
                }
              }
            }}
          />
          <FGListen
            field="postalCode"
            onChanged={(value) => {
              if (isBelgium) {
                setPostalCode(value);
              }
            }}
          />
          <DrupalFieldSet title={t(ETLCodes.Contact)}>
            <FieldGroup columns={2}>
              <div>
                <FGSelectInput
                  name="personHonorificId"
                  items={personSalutations}
                  loading={ploading}
                  label={t(ETLCodes.Salutation)}
                />
                <FGSelectInput
                  name="languageId"
                  items={languages}
                  loading={lloading}
                  label={t(ETLCodes.Language)}
                  requiredMark
                />
                <FGTextInput name="lastName" label={t(ETLCodes.LastName)} readonly />
                <FGTextInput name="firstName" label={t(ETLCodes.FirstName)} readonly />
                <FGTextInput name="email" label={t(ETLCodes.Email)} requiredMark readonly />
                <FGPhoneInput name="phoneNumber" label={t(ETLCodes.PhoneNumber)} requiredMark />
                <FGPhoneInput name="mobilePhoneNumber" label={t(ETLCodes.MobilePhoneNumber)} requiredMark />
                <FGTextInput name="street" label={t(ETLCodes.Street)} />
                <FGTextInput name="number" label={t(ETLCodes.StreetNumber)} />
                {isBelgium ? (
                  <FGSelectInput
                    name="postalCode"
                    items={postalCodes}
                    loading={pcloading}
                    label={t(ETLCodes.PostalCode)}
                  />
                ) : (
                  <FGTextInput name="postalCode" label={t(ETLCodes.PostalCode)} />
                )}
                {isBelgium ? (
                  <FGSelectInput name="city" items={localities} loading={locloading} label={t(ETLCodes.City)} />
                ) : (
                  <FGTextInput name="city" label={t(ETLCodes.City)} />
                )}
                <FGCountrySelect name="countryId" label={t(ETLCodes.Country)} />
                <FGTextInput name="hkNumber" label={t(ETLCodes.hkNumber)} />
                <FGTextInput name="kennelName" label={t(ETLCodes.Kennel)} readonly />
              </div>
            </FieldGroup>
          </DrupalFieldSet>
        </DrupalFormGenerator>
      </>
    )
  );
};
